import { Typography, useTheme } from "@material-ui/core";
import { differenceInSeconds, parseISO } from "date-fns";
import { useRef } from "react";
import { useHistory } from "react-router-dom";
import resourceNames from "../../common/data/resourceNames";
import { DashboardDriverHosEventsOutput } from "../../common/data/service-proxies";
import { HosEventLine } from "../hos/logs/driver-logs/HosEventLine";
import { TimeOfDayIndicator } from "./TimeOfDayIndicator";

interface Props {
  date: Date;
  position?: number;
  hosEvents: DashboardDriverHosEventsOutput;
}

const getPosition = (position?: number, width?: number) => {
  if (!position || !width) {
    return 0;
  }

  const maxSeconds = 24 * 60 * 60;
  return (width * position) / maxSeconds;
};

export const DashboardHosGraph = ({ date, position, hosEvents }: Props) => {
  const history = useHistory();
  const theme = useTheme();
  const graphRef = useRef<HTMLDivElement>(null);
  let { startOfDay, hoursToday = 24, basicEvents, id: driverId } = hosEvents;
  if (basicEvents && startOfDay && basicEvents[0].startTime! > startOfDay) {
    basicEvents = [
      {
        id: 0,
        dutyStatus: "OFF",
        locationDisplayName: "",
        durationInSeconds: differenceInSeconds(
          parseISO(basicEvents[0].startTime!),
          parseISO(startOfDay)
        ),
        startTime: startOfDay,
        endTime: basicEvents[0].startTime,
      },
      ...basicEvents,
    ];
  }

  return (
    <div className="control hos-control">
      <div className="control-label">
        <Typography variant="body2" color="textSecondary">
          {hosEvents.driverFullName ? <>{hosEvents.driverFullName}'s</> : null}{" "}
          Logbook
          {hosEvents.driverId ? <> ({hosEvents.driverId})</> : null}
        </Typography>
      </div>
      <div className="graph-container">
        {basicEvents && startOfDay ? (
          <>
            <div className="status-labels">
              <Typography variant="caption" color="textSecondary">
                OF
              </Typography>
              <Typography variant="caption" color="textSecondary">
                SB
              </Typography>
              <Typography variant="caption" color="textSecondary">
                DR
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ON
              </Typography>
            </div>
            <div
              className="graph"
              ref={graphRef}
              onClick={() =>
                history.push(
                  `/${
                    resourceNames.HOS_DRIVER_LOG_VIEW
                  }/${driverId}/${date.toISOString()}`
                )
              }>
              <TimeOfDayIndicator
                position={getPosition(position, graphRef.current?.clientWidth)}
                height={graphRef.current?.clientHeight}
              />
              <svg
                viewBox={`0 0 ${hoursToday * 50} 160`}
                preserveAspectRatio="none"
                style={{ zIndex: "1" }}>
                <defs>
                  <pattern
                    id="background"
                    width={`${(50 / (50 * hoursToday)) * 100}%`}
                    height="100%">
                    <rect fill="none" x="0" y="0" width="50" height="160" />
                    <g
                      className="vertical-lines"
                      style={{
                        stroke: theme.palette.text.disabled,
                        strokeWidth: 0.8,
                      }}>
                      <line x1="0" y1="0" x2="0" y2="160" />
                      <g id="rows" style={{ strokeWidth: 0.5 }}>
                        <g id="row-1">
                          <line x1="12.5" y1="0" x2="12.5" y2="20" />
                          <line x1="25" y1="0" x2="25" y2="30" />
                          <line x1="37.5" y1="0" x2="37.5" y2="20" />
                        </g>
                        <g id="row-2">
                          <line x1="12.5" y1="40" x2="12.5" y2="60" />
                          <line x1="25" y1="40" x2="25" y2="70" />
                          <line x1="37.5" y1="40" x2="37.5" y2="60" />
                        </g>
                        <g id="row-3">
                          <line x1="12.5" y1="100" x2="12.5" y2="120" />
                          <line x1="25" y1="90" x2="25" y2="120" />
                          <line x1="37.5" y1="100" x2="37.5" y2="120" />
                        </g>
                        <g id="row-4">
                          <line x1="12.5" y1="140" x2="12.5" y2="160" />
                          <line x1="25" y1="130" x2="25" y2="160" />
                          <line x1="37.5" y1="140" x2="37.5" y2="160" />
                        </g>
                      </g>
                    </g>
                    <g
                      className="horizontal-lines"
                      style={{
                        stroke: theme.palette.text.disabled,
                        strokeWidth: 0.8,
                      }}>
                      <line x1="0" y1="40" x2="50" y2="40" />
                      <line x1="0" y1="80" x2="50" y2="80" />
                      <line x1="0" y1="120" x2="50" y2="120" />
                    </g>
                  </pattern>
                </defs>
                <rect
                  id="frame"
                  x="0"
                  y="0"
                  width={`${50 * hoursToday}`}
                  height="160"
                  fill="none"
                  style={{
                    stroke: theme.palette.text.secondary,
                  }}></rect>
                <rect
                  x="0"
                  y="0"
                  height="160"
                  width={`${50 * hoursToday}`}
                  fill="url(#background)"
                />
                <g id="hos-graph">
                  {basicEvents.map((event, index: number) => {
                    return (
                      <HosEventLine
                        noHighlight
                        offsetX={0}
                        offsetY={-60}
                        key={event.id}
                        id={event.id!}
                        index={index}
                        dutyStatus={event.dutyStatus}
                        startTime={event.startTime!}
                        endTime={event.endTime!}
                        lineColor={theme.palette.primary.main}
                        basicEvents={[
                          ...basicEvents!.map((x) => ({
                            ...x,
                            lineColor: theme.palette.text.primary,
                          })),
                        ]}
                        startOfDay={startOfDay}
                      />
                    );
                  })}
                </g>
              </svg>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
